/**
 * Library — Layout route wrapping child pages
 *
 * 5 tabs: Favorites (default), Documents, Sources (admin), Quality, System
 *
 * PR-J adds the System tab: read-only view of platform-published
 * Library content (system rows authored by sysadmin and published).
 * System rows are badged "System" with attribution "Platform Admin"
 * — tenants cannot edit/delete them.
 *
 * Tabs hidden on sub-pages (source detail, document detail, article detail, connect wizard).
 *
 * P1 #3 (Round 1 Library audit, 2026-05-13) — The top-bar "+ New"
 * dropdown was removed because the shared <LibraryDocumentsView> body
 * already renders a "+ New" inside the "All Documents" card, producing
 * two primary CTAs in the same viewport on /library/documents. The
 * card-level button is kept (it travels with the table to sysadmin
 * `/sysadmin/catalog/library` as well, preserving UI parity), so the
 * layout no longer needs to duplicate the action. Other tabs
 * (Favorites / Quality / System) don't need a top-bar "+ New" — their
 * own empty/landing copy points users at /library/documents.
 */

import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { ContentHeader } from '@/components/content-header';
import { Star, FileText, HardDrive, FlaskConical, Globe, Search, Share2 } from 'lucide-react';
import { useHasPermission } from '@/lib/use-has-permission';

export const Route = createFileRoute('/library')({
  component: LibraryLayout,
});

// Tab paths where the header should show
const TAB_PATHS = [
  '/library', '/library/',
  '/library/documents', '/library/documents/',
  '/library/sources', '/library/sources/',
  '/library/quality', '/library/quality/',
  '/library/system', '/library/system/',
  // #139 — production semantic Search now has its own tab.
  '/library/search', '/library/search/',
  // #142 — Shared with me is now reachable from the Library nav
  // instead of only via direct URL.
  '/library/shared', '/library/shared/',
];

function LibraryLayout() {
  const location = useLocation();
  // The Sources tab asks the key the Sources PAGE asks and the sources list
  // route declares — read:library.documents (GET /library/connectors;
  // LIBRARY_CONTROLS.viewSources in @argusai/shared). LB6-T3 review R1: it
  // asked write:library (the CONNECT key) after the page moved to the read
  // key, so a Member was shown the page by URL and never offered the tab.
  // Connecting a source still asks write:library, on the page itself.
  const canViewSources = useHasPermission('read', 'library.documents');
  const isTabPage = TAB_PATHS.includes(location.pathname);

  const tabs = [
    { label: 'Favorites', path: '/library', icon: Star },
    { label: 'Documents', path: '/library/documents', icon: FileText },
    // #139 — production semantic Search across the unified corpus
    // (uploaded documents, wiki articles, external synced docs).
    // Distinct from the power-user Quality → Search Tester diagnostic
    // which exposes raw dense/sparse score breakdowns.
    { label: 'Search', path: '/library/search', icon: Search },
    // #142 — Shared with me is part of the normal Library IA, not a
    // hidden direct-URL-only page.
    { label: 'Shared', path: '/library/shared', icon: Share2 },
    ...(canViewSources ? [{ label: 'Sources', path: '/library/sources', icon: HardDrive }] : []),
    { label: 'Quality', path: '/library/quality', icon: FlaskConical },
    // PR-J — System tab. Always visible to all roles; the underlying
    // content is published by sysadmin and tenants get read-only access.
    { label: 'System', path: '/library/system', icon: Globe },
  ];

  return (
    <div className="flex flex-col h-full">
      {isTabPage && (
        <ContentHeader
          title="Library"
          tabs={tabs}
        />
      )}
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
